import '../styles/globals.css'
import '../styles/react-grid-layout.css'
// import 'react-grid-layout/css/styles.css'
// import 'react-resizable/css/styles.css'
import '@solana/wallet-adapter-react-ui/styles.css'

import 'utils/polyfills'

import type { AppProps } from 'next/app'
import {
  Adapter,
  WalletError,
  WalletNotReadyError,
} from '@solana/wallet-adapter-base'
import {
  ConnectionProvider,
  WalletProvider,
} from '@solana/wallet-adapter-react'
import { ThemeProvider } from 'next-themes'
import { appWithTranslation } from 'next-i18next'
import Layout from '@components/layout/Layout'
import { QueryClient, QueryClientProvider } from '@tanstack/react-query'
import { ToastContainer } from 'react-toastify'
import 'react-toastify/dist/ReactToastify.css'
import { useLocalStorage } from 'hooks/useLocalStorageState'
import { AUTO_CONNECT_WALLET } from 'utils/constants'
import Provider from '@components/Provider'
import Head from 'next/head'
import TransactionNotification from '@components/notifications/TransactionNotification'
import { WalletModalProvider } from '@solana/wallet-adapter-react-ui'
import { Telemetry } from '@components/Telemetry'
import store from 'stores/store'
import {
  PhantomWalletAdapter,
  SolflareWalletAdapter,
} from '@solana/wallet-adapter-wallets'
import { createDefaultAddressSelector, createDefaultAuthorizationResultCache, createDefaultWalletNotFoundHandler, SolanaMobileWalletAdapter } from '@solana-mobile/wallet-adapter-mobile'

const queryClient = new QueryClient()
function onWalletError(error: WalletError, adapter?: Adapter) {
  console.error(`Wallet error: adapter.name=${adapter?.name}`, error)
  if (error instanceof WalletNotReadyError && adapter) {
    console.log({
      title: `${adapter.name} Error`,
      type: 'error',
      description: `Please install ${adapter.name} and then reload this page.`,
    })
    if (typeof window !== 'undefined') {
      window.open(adapter.url, '_blank', 'noopener,noreferrer')
    }
  }
}

// Do not add hooks to this component that will cause unnecessary rerenders
// Top level state hydrating/updating should go in Provider

function MyApp({
  Component,
  pageProps,
}: AppProps<{
  metaTitle?: string
  metaDescription?: string
  metaShareImage?: string
  metaShareImageAlt?: string
  metaShareImageWidth?: number
  metaShareImageHeight?: number
}>) {
  const [autoConnect] = useLocalStorage(AUTO_CONNECT_WALLET)
  const endpointUrl = store((s) => s.endpointUrl)

  const title = pageProps?.metaTitle || 'Cabana Exchange'
  const description =
    pageProps?.metaDescription ||
    'Swap/trade any Solana token at the best possible price.'
  const shareImageUrl =
    pageProps?.metaShareImage ||
    'https://cabana.exchange/twitter-share-1200x675.png'
  const shareImageAlt = pageProps?.metaShareImageAlt || 'Cabana Exchange'
  const shareImageWidth = pageProps?.metaShareImageWidth || 1200
  const shareImageHeight = pageProps?.metaShareImageHeight || 675

  return (
    <>
      <Head>
        <link rel="icon" href="/favicon.ico" sizes="any" />
        <link
          rel="icon"
          type="image/png"
          href="/favicon-32x32.png"
          sizes="32x32"
        />
        <link
          rel="icon"
          type="image/png"
          href="/favicon-64x64.png"
          sizes="64x64"
        />
        <link
          rel="icon"
          type="image/png"
          href="/favicon-128x128.png"
          sizes="128x128"
        />
        <link
          rel="icon"
          type="image/png"
          href="/favicon-192x192.png"
          sizes="192x192"
        />
        <link
          rel="icon"
          type="image/png"
          href="/favicon-512x512.png"
          sizes="512x512"
        />
        <link
          rel="apple-touch-icon"
          sizes="192x192"
          href="/apple-touch-icon.png"
        />
        <link rel="manifest" href="/manifest.json" />
        <meta name="viewport" content="width=device-width, initial-scale=1" />
        <meta name="msapplication-TileColor" content="#da532c" />
        <meta name="theme-color" content="#ffffff" />

        <meta
          name="keywords"
          content="Cabana Exchange, Cabana DEX, Decentralized Exchange, token swapping, non-custodial trading, yield farming, liquidity pools, secure DeFi exchange, low-fee crypto trading, solana, sol, usdc, exchange, dex, swap, router, defi, cabana, cabana.exchange"
        />

        <title>{title}</title>
        <meta name="description" content={description} />

        <meta property="og:title" content={title} />
        <meta property="og:description" content={description} />
        <meta property="og:image" content={shareImageUrl} />
        <meta property="og:image:alt" content={shareImageAlt} />
        <meta property="og:image:width" content={String(shareImageWidth)} />
        <meta property="og:image:height" content={String(shareImageHeight)} />

        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={title} />
        <meta name="twitter:description" content={description} />
        <meta name="twitter:image" content={shareImageUrl} />

        <meta name="google" content="notranslate" />
      </Head>
      <QueryClientProvider client={queryClient}>
        <ConnectionProvider endpoint={endpointUrl}>
          <WalletProvider
            autoConnect={autoConnect}
            wallets={[
              new SolanaMobileWalletAdapter({
                addressSelector: createDefaultAddressSelector(),
                appIdentity: {
                  name: 'Cabana Exchange',
                  uri: 'https://cabana.exchange',
                  icon: '/favicon-192x192.png',
                },
                authorizationResultCache: createDefaultAuthorizationResultCache(),
                cluster: 'mainnet-beta',
                onWalletNotFound: createDefaultWalletNotFoundHandler(),
              }),
              new PhantomWalletAdapter(),
              new SolflareWalletAdapter(),
            ]}
            onError={onWalletError}
          >
            <WalletModalProvider>
              <Provider />
              <ThemeProvider defaultTheme="Dark">
                <Layout>
                  <Telemetry>
                    <Component {...pageProps} />
                  </Telemetry>
                  <ToastContainer />
                </Layout>
                <TransactionNotification />
              </ThemeProvider>
            </WalletModalProvider>
          </WalletProvider>
        </ConnectionProvider>
      </QueryClientProvider>
    </>
  )
}

export default appWithTranslation(MyApp)
